import React, { useState, useEffect, useMemo } from 'react';
import { stocktakeSessionsService, beverageProductsService } from '../../services';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { useToast } from '@/hooks/use-toast';
import {
  Calculator, Loader2, ShoppingCart, AlertTriangle,
  BarChart3, ArrowDown, ArrowUp, CheckCircle, ChevronRight, Activity,
  Search, X,
} from 'lucide-react';
import { formatCurrency, formatAccounting, formatQtyAccounting, roundHalfUp } from './stockAuditUtils';
import { getApiError } from '@/lib/utils';
import { StockAuditBreakdownDialog, buildBreakdown } from './StockAuditBreakdownDialog';

// iter767 — Bug-3 reverted: the previous "Bug-3 fix" was incorrectly subtracting
// management_sales_incl_vat from Net ESR everywhere (deriveNetEsrIncl + Part 2 waterfall),
// which caused the modal to show −£1,716.12 while the headline correctly showed −£2,900.72.
// Correct view: Net ESR = Gross − Allowances ONLY. Management PLU is a result-stage
// DEDUCTION shown as its own row in the Part 2 waterfall.
function deriveNetEsrIncl(calc) {
  // iter767 — Net ESR = Gross − Allowances ONLY.
  // Management PLU is a result-stage deduction (applied AFTER the Trading
  // Shortfall step), not an ESR reduction. Removing the management
  // subtraction that was added as "Bug-3 fix" because it caused the modal
  // to show −£1,716.12 (wrong) vs the correct headline −£2,900.72.
  if (!calc) return null;
  const stored = calc.estimated_receipts_incl_vat;
  const gross   = calc.estimated_receipts_incl_vat_gross;
  const allow   = Math.abs(calc.allowance_value || 0);

  // Engine reported Estimated Receipts as unavailable — never derive a figure.
  if (stored == null && calc.headline_fallback_reason === 'no_estimated_receipts') return null;
  if (stored != null) return stored;   // Gross − Allowances, correctly set by engine
  if (gross != null) return +(Number(gross) - allow).toFixed(2);  // fallback derivation
  return calc.estimated_receipts_excl_vat ?? null;
}


export function StockAuditResultsPanel({ sessionId, status, countedLines, dirty, onSave, onValidationUpdate, isCellarSession, isLocationSession, hasCellar = true, isReadOnly = false, costBlocked = false }) {
  const [calculation, setCalculation] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [blockerMessage, setBlockerMessage] = useState(null);
  const [costBlocker, setCostBlocker] = useState(null);
  const [breakdownType, setBreakdownType] = useState(null);
  const [warningsExpanded, setWarningsExpanded] = useState(false);
  const [localValidation, setLocalValidation] = useState(null);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [draftCheck, setDraftCheck] = useState(null);
  const [showDraftDialog, setShowDraftDialog] = useState(false);
  const [postingDrafts, setPostingDrafts] = useState(false);
  const [sessionKpis, setSessionKpis] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [costingReport, setCostingReport] = useState(null);
  const [movementTrailOpen, setMovementTrailOpen] = useState(false);
  // Analysis table filters (same methodology as Sales / Deliveries wizard)
  const [productCatalog, setProductCatalog] = useState([]); // full catalog — used for category/subcategory lookup
  const [movementSearch, setMovementSearch] = useState('');
  const [movementSelectedCats, setMovementSelectedCats] = useState([]);
  const [movementSelectedSubs, setMovementSelectedSubs] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [productSelectedCats, setProductSelectedCats] = useState([]);
  const [productSelectedSubs, setProductSelectedSubs] = useState([]);
  const { toast } = useToast();

  useEffect(() => {
    stocktakeSessionsService.getCalculation(sessionId).then(data => {
      if (data?.snapshot) {
        setCalculation(data.snapshot);
      }
    }).catch(() => {});
    // Pre-deployment defect sweep #4 — fire the KPI/analytics fetch in
    // PARALLEL with the calculation fetch (not chained after it) so the
    // Yield % tile doesn't flash an incorrect "—" for an extra
    // round-trip once the KPI panel first renders.
    loadAnalytics();
  }, [sessionId]);

  // Load product catalog for category/subcategory lookup (matches Sales/Deliveries wizards).
  // iter360 — no status filter; we need ALL products (incl. legacy / NULL-status) to resolve cat/subcat for closing audit rows.
  useEffect(() => {
    beverageProductsService.listAll({})
      .then(items => setProductCatalog(Array.isArray(items) ? items : (items?.items || [])))
      .catch(() => setProductCatalog([]));
  }, []);

  // Product → {category, subcategory} map
  const productMetaMap = useMemo(() => {
    const map = {};
    productCatalog.forEach(p => {
      const cat = typeof p.category === 'object' ? (p.category?.value || p.category) : p.category;
      map[p.id] = { category: cat || null, subcategory: p.subcategory || null, product_code: p.product_code || null };
    });
    return map;
  }, [productCatalog]);

  // --- Stock Audit Analytics filter options (from costingReport.products) ---
  const movementCategories = useMemo(() => {
    const cats = new Set();
    (costingReport?.products || []).forEach(p => {
      const cat = p.category || productMetaMap[p.product_id]?.category;
      if (cat) cats.add(cat);
    });
    return [...cats].sort();
  }, [costingReport, productMetaMap]);

  const movementSubcategories = useMemo(() => {
    const subs = new Set();
    (costingReport?.products || []).forEach(p => {
      const sub = p.subcategory || productMetaMap[p.product_id]?.subcategory;
      if (sub) subs.add(sub);
    });
    return [...subs].sort();
  }, [costingReport, productMetaMap]);

  const filteredMovementProducts = useMemo(() => {
    const rows = costingReport?.products || [];
    const q = movementSearch.trim().toLowerCase();
    return rows.filter(p => {
      const meta = productMetaMap[p.product_id] || {};
      const cat = p.category || meta.category;
      const sub = p.subcategory || meta.subcategory;
      const code = p.product_code || meta.product_code || '';
      if (movementSelectedCats.length > 0 && (!cat || !movementSelectedCats.includes(cat))) return false;
      if (movementSelectedSubs.length > 0 && (!sub || !movementSelectedSubs.includes(sub))) return false;
      if (q) {
        const name = (p.product_name || '').toLowerCase();
        const codeL = String(code).toLowerCase();
        if (!name.includes(q) && !codeL.includes(q)) return false;
      }
      return true;
    });
  }, [costingReport, productMetaMap, movementSearch, movementSelectedCats, movementSelectedSubs]);

  const movementActiveFilters = movementSelectedCats.length + movementSelectedSubs.length;

  // --- Product Analytics filter options (from calculation.lines) ---
  // calculation.lines has beverage_product_id on count_sheet_line_id but we need to look up
  // product via productMetaMap (since lines don't carry category).
  const productLineCategories = useMemo(() => {
    const cats = new Set();
    (calculation?.lines || []).forEach(ln => {
      const meta = productMetaMap[ln.beverage_product_id];
      if (meta?.category) cats.add(meta.category);
    });
    return [...cats].sort();
  }, [calculation, productMetaMap]);

  const productLineSubcategories = useMemo(() => {
    const subs = new Set();
    (calculation?.lines || []).forEach(ln => {
      const meta = productMetaMap[ln.beverage_product_id];
      if (meta?.subcategory) subs.add(meta.subcategory);
    });
    return [...subs].sort();
  }, [calculation, productMetaMap]);

  const filteredCalculationLines = useMemo(() => {
    const rows = calculation?.lines || [];
    const q = productSearch.trim().toLowerCase();
    return rows.filter(ln => {
      const meta = productMetaMap[ln.beverage_product_id] || {};
      if (productSelectedCats.length > 0 && (!meta.category || !productSelectedCats.includes(meta.category))) return false;
      if (productSelectedSubs.length > 0 && (!meta.subcategory || !productSelectedSubs.includes(meta.subcategory))) return false;
      if (q) {
        const name = (ln.product_name || '').toLowerCase();
        const code = String(meta.product_code || '').toLowerCase();
        if (!name.includes(q) && !code.includes(q)) return false;
      }
      return true;
    });
  }, [calculation, productMetaMap, productSearch, productSelectedCats, productSelectedSubs]);

  const productActiveFilters = productSelectedCats.length + productSelectedSubs.length;

  const loadAnalytics = async () => {
    try {
      const [kpiData, costData] = await Promise.all([
        stocktakeSessionsService.getSessionKpis(sessionId).catch(() => null),
        stocktakeSessionsService.getCostingReport(sessionId).catch(() => null),
      ]);
      if (kpiData) setSessionKpis(kpiData);
      if (costData) setCostingReport(costData);
    } catch { /* silent */ } finally {
      setAnalyticsLoading(false);
    }
  };

  const runCalculation = async () => {
    setCalculating(true);
    setBlockerMessage(null);
    setCostBlocker(null);
    try {
      if (dirty && onSave) await onSave(false);
      const result = await stocktakeSessionsService.calculateAudit(sessionId, 'weighted_average');
      setCalculation(result);
      // 2026-05-08 (iter340) — Toast must show the SAME figure as the KPI
      // box. Previously displayed `stocktake_result` (raw legacy
      // actual − expected) which differed from the headline-adjusted
      // figure used elsewhere, causing the "two different Result KPIs"
      // bug reported on Mercure Brighton.
      const headlineForToast = (
        result.headline_stocktake_result
        ?? result.stocktake_result_revised_adjusted
        ?? result.stocktake_result_legacy_adjusted
        ?? result.stocktake_result
      );
      toast({ title: 'Stock Audit Calculated', description: `Stocktake result: ${formatCurrency(headlineForToast)}` });
      await loadAnalytics();
      try {
        const vr = await stocktakeSessionsService.validateSession(sessionId);
        setLocalValidation(vr);
        if (onValidationUpdate) onValidationUpdate(vr);
      } catch { /* silent */ }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 422 && detail?.error === 'unresolved_missing_costs') {
        setCostBlocker({ message: detail.message, lines: detail.lines || [] });
        toast({ title: 'Calculation blocked — missing costs', description: detail.message, variant: 'destructive' });
        return;
      }
      const msg = getApiError(err, 'Failed');
      // Strict Revised-methodology enforcement: the backend raises
      // "ACCOUNTS_SALES_REQUIRED: ..." when Actual Receipts (excl VAT) is
      // missing on the session. Surface this as a persistent blocker banner
      // so the user knows exactly what to do — no silent fallback.
      if (typeof msg === 'string' && msg.includes('ACCOUNTS_SALES_REQUIRED')) {
        const friendly = msg.replace(/^.*ACCOUNTS_SALES_REQUIRED:\s*/, '');
        setBlockerMessage(friendly);
        toast({
          title: 'Actual Receipts required',
          description: 'Enter Actual Receipts (excl. VAT) in the Actual Receipts panel before calculating.',
          variant: 'destructive',
        });
      } else if (typeof msg === 'string' && msg.includes('MANAGEMENT_COS_BUDGET_REQUIRED')) {
        const friendly = msg.replace(/^.*MANAGEMENT_COS_BUDGET_REQUIRED:\s*/, '');
        setBlockerMessage(friendly);
        toast({
          title: 'Site COS Budget % required',
          description: 'Configure the Beverages COS Budget % on the Site Master, then retry the calculation.',
          variant: 'destructive',
        });
      } else {
        toast({ title: 'Calculation Failed', description: msg, variant: 'destructive' });
      }
    } finally { setCalculating(false); }
  };

  const handleCalculate = async () => {
    try {
      const check = await stocktakeSessionsService.preCalculateCheck(sessionId);
      // D6 — Block calculation when any POSTED delivery in this session has a
      // header total that disagrees with its actual line sum (likely a concurrent-
      // save duplicate).  This prevents the engine silently consuming an inflated
      // deliveries_value that would contradict what the DN register shows.
      if (check.has_inconsistent_deliveries) {
        const details = (check.inconsistent_deliveries || [])
          .map(d => `DN ${d.reference_number}: header £${(d.stored_header_total||0).toFixed(2)} vs lines £${(d.actual_lines_sum||0).toFixed(2)} (gap ${d.gap > 0 ? '+' : ''}£${(d.gap||0).toFixed(2)})`)
          .join('\n');
        toast({
          title: 'Delivery Note Inconsistency — Calculation Blocked',
          description: `${check.inconsistent_deliveries.length} delivery note(s) have a header total that doesn't match their lines. Remove the duplicate line(s) before recalculating.\n${details}`,
          variant: 'destructive',
          duration: 10000,
        });
        return;
      }
      if (check.has_unresolved_exceptions) {
        const exc = check.unresolved_exceptions || [];
        const message = `${exc.length} counted line(s) have a nonzero quantity but no resolvable cost. Enter a unit cost (or £0 for FOC) on the Closing Stock step.`;
        setCostBlocker({ message, lines: exc.map(e => `${e.product_name} (${e.quantity} ${e.unit_label || 'units'})`) });
        toast({ title: 'Calculation blocked — missing costs', description: message, variant: 'destructive' });
        return;
      }
      setCostBlocker(null);
      if (check.has_drafts) {
        setDraftCheck(check);
        setShowDraftDialog(true);
        return;
      }
    } catch (err) {
      console.warn('Pre-calculate check failed, proceeding:', err);
    }
    await runCalculation();
  };

  const handlePostAllAndCalculate = async () => {
    setPostingDrafts(true);
    try {
      const result = await stocktakeSessionsService.postAllDrafts(sessionId);
      if (result.errors?.length > 0) {
        toast({ title: 'Some transactions could not be posted', description: result.errors.join('; '), variant: 'destructive' });
      }
      if (result.posted_count > 0) {
        toast({ title: `${result.posted_count} transaction${result.posted_count > 1 ? 's' : ''} posted` });
      }
    } catch (err) {
      toast({ title: 'Failed to post drafts', description: getApiError(err, 'Error'), variant: 'destructive' });
    } finally { setPostingDrafts(false); }
    setShowDraftDialog(false);
    setDraftCheck(null);
    await runCalculation();
  };

  const handleExcludeAndCalculate = async () => {
    setShowDraftDialog(false);
    setDraftCheck(null);
    await runCalculation();
  };

  const calcType = calculation?.calculation_type || 'stock_audit';
  const isAudit = calcType === 'stock_audit';

  // Build the breakdown data for the dialog on-demand (all inputs are
  // already in state, no extra API call needed).
  const activeBreakdown = useMemo(() => {
    if (!breakdownType) return null;
    try {
      return buildBreakdown(breakdownType, {
        calculation,
        costingReport,
        sessionKpis,
        isCellarSession,
        isLocationSession,
        hasCellar,
      });
    } catch (err) {
      console.error('[StockAuditResultsPanel] buildBreakdown error', err);
      return null;
    }
  }, [breakdownType, calculation, costingReport, sessionKpis, isCellarSession, isLocationSession, hasCellar]);

  // Clickable figure — wraps any number/badge in a button that opens the
  // breakdown dialog. Preserves surrounding formatting (tabular-nums etc.)
  // through `className`. `disabled` hides the underline when there's no
  // data source (e.g. calculation not yet run).
  const ClickableFigure = ({ type, children, className = '', disabled = false, testId }) => {
    if (disabled) return <span className={className}>{children}</span>;
    return (
      <button
        type="button"
        onClick={() => setBreakdownType(type)}
        data-testid={testId || `breakdown-trigger-${type}`}
        className={`${className} underline decoration-dotted decoration-slate-400 underline-offset-2 hover:decoration-slate-700 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-slate-400 rounded-sm cursor-pointer`}
      >
        {children}
      </button>
    );
  };

  return (
    <Card data-testid="stock-audit-results-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-emerald-600" />
            Stock Audit Calculation
          </CardTitle>
          <div className="flex items-center gap-2">
            {/* Wave 5A P6: Calculate button hidden for PSB historical read-only sessions */}
            {!isReadOnly && (
            <Button
              size="sm"
              onClick={handleCalculate}
              disabled={calculating || countedLines === 0 || costBlocked || status === 'locked'}
              title={
                status === 'locked' ? 'Session is locked and published — its result is frozen'
                  : costBlocked ? 'Resolve missing costs on Closing Stock before calculating'
                  : countedLines === 0 ? 'No closing count entries yet — enter at least one count on the Closing Stock step before calculating'
                  : undefined
              }
              data-testid="calculate-audit-btn"
            >
              {calculating ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Calculator className="h-3 w-3 mr-1" />}
              {calculation ? 'Recalculate' : 'Calculate'}
            </Button>
            )}
          </div>
        </div>
        {calculation && (
          <CardDescription className="text-xs">
            Version {calculation.version} | {calculation.calculated_at ? new Date(calculation.calculated_at).toLocaleString('en-GB') : ''} | {(calculation.cost_basis_method || '').replace('_', ' ')}
            {calculation.as_of_date && <span className="ml-1">| Costs as of {calculation.as_of_date}</span>}
          </CardDescription>
        )}
        {!isReadOnly && !calculating && countedLines === 0 && !costBlocked && (
          <p className="text-xs text-amber-600 mt-1" data-testid="no-closing-counts-hint">
            Calculate is disabled — no closing count entries yet. Enter at least one count on the Closing Stock step.
          </p>
        )}
      </CardHeader>

      {/* 2026-11 — read-only staleness gate: the saved calculation's own
          Deliveries total no longer matches a fresh read of the live
          delivery rows (e.g. a posted delivery/credit within the period was
          deleted or re-pinned AFTER this snapshot was calculated). Never
          auto-recalculates — just tells the auditor the headline figures
          below may not reconcile with the breakdown and offers Recalculate. */}
      {calculation?.self_consistency?.stale && (
        <CardContent className="pt-0 pb-4">
          <div className="border-2 border-amber-300 rounded-lg bg-amber-50 p-4 flex items-start gap-3" data-testid="stale-calculation-banner">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="space-y-2 flex-1">
              <p className="font-semibold text-sm text-amber-800">Saved calculation may be out of date</p>
              <p className="text-xs text-amber-700 leading-relaxed">{calculation.self_consistency.message}</p>
              {!isReadOnly && (
                <Button
                  size="sm" variant="outline"
                  className="h-7 text-xs border-amber-400 text-amber-800 hover:bg-amber-100"
                  onClick={handleCalculate}
                  data-testid="stale-calculation-recalculate-btn"
                >
                  <Calculator className="h-3 w-3 mr-1" />Recalculate now
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      )}

      {/* Strict-methodology blocker banner: shown when the backend refuses to
          run the calculation because the org has selected the Actual Receipts
          methodology but Actual Receipts (excl VAT) is missing on the session.
          There is NO silent fallback to Legacy — the user must enter Actual
          Receipts to continue. */}
      {costBlocker && (
        <CardContent className="pt-0 pb-4">
          <div className="border-2 border-red-300 rounded-lg bg-red-50 p-4 flex items-start gap-3" data-testid="missing-cost-blocker-banner">
            <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
            <div className="space-y-1">
              <p className="font-semibold text-sm text-red-800">Calculation blocked — missing costs</p>
              <p className="text-xs text-red-700 leading-relaxed">{costBlocker.message}</p>
              <ul className="text-xs text-red-800 list-disc pl-4" data-testid="missing-cost-blocker-lines">
                {costBlocker.lines.map(l => <li key={l}>{l}</li>)}
              </ul>
            </div>
          </div>
        </CardContent>
      )}
      {blockerMessage && (
        <CardContent className="pt-0 pb-4">
          <div
            className="border-2 border-red-300 rounded-lg bg-red-50 p-4 flex items-start gap-3"
            data-testid="accounts-sales-required-banner"
          >
            <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
            <div className="space-y-1">
              <p className="font-semibold text-sm text-red-800">
                Calculation blocked — Actual Receipts required
              </p>
              <p className="text-xs text-red-700 leading-relaxed">
                {blockerMessage}
              </p>
              <p className="text-xs text-red-700 leading-relaxed">
                Open the <strong>Actual Receipts</strong> panel for this session
                and enter the figure (excl. VAT), then run the calculation
                again. No silent fallback to the Legacy (PLU-led) methodology
                is permitted.
              </p>
            </div>
          </div>
        </CardContent>
      )}

      {/* Draft/Unposted Transactions Dialog */}
      {showDraftDialog && draftCheck && (
        <CardContent className="pt-0 pb-4">
          <div className="border-2 border-amber-300 rounded-lg bg-amber-50 p-4 space-y-3" data-testid="draft-transactions-dialog">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <span className="font-medium text-sm text-amber-800">
                {draftCheck.total_draft_count} Unposted Transaction{draftCheck.total_draft_count !== 1 ? 's' : ''} Found
              </span>
            </div>
            <p className="text-xs text-amber-700">
              The following draft transactions are within the audit period but will be <strong>excluded</strong> from the calculation unless posted. Please review and confirm.
            </p>

            {draftCheck.draft_deliveries.length > 0 && (
              <div className="space-y-1">
                <span className="text-xs font-medium text-slate-700 flex items-center gap-1">
                  <ArrowDown className="h-3 w-3 text-emerald-500" /> Draft Deliveries ({draftCheck.draft_deliveries.length})
                </span>
                {draftCheck.draft_deliveries.map(d => (
                  <div key={`del-${d.id}`} className="flex items-center justify-between bg-white rounded px-3 py-1.5 border text-xs" data-testid={`draft-delivery-${d.id}`}>
                    <span>{d.reference_number || `Delivery #${d.id}`} — {d.date}</span>
                    <span className="font-medium tabular-nums">{formatCurrency(d.lines_total)}</span>
                  </div>
                ))}
              </div>
            )}

            {draftCheck.draft_credits.length > 0 && (
              <div className="space-y-1">
                <span className="text-xs font-medium text-slate-700 flex items-center gap-1">
                  <ArrowUp className="h-3 w-3 text-red-500" /> Draft Credits ({draftCheck.draft_credits.length})
                </span>
                {draftCheck.draft_credits.map(c => (
                  <div key={`cred-${c.id}`} className="flex items-center justify-between bg-white rounded px-3 py-1.5 border text-xs" data-testid={`draft-credit-${c.id}`}>
                    <span>{c.reference_number || `Credit #${c.id}`} — {c.date}</span>
                    <span className="font-medium tabular-nums">{formatCurrency(c.lines_total)}</span>
                  </div>
                ))}
              </div>
            )}

            {draftCheck.draft_sales.length > 0 && (
              <div className="space-y-1">
                <span className="text-xs font-medium text-slate-700 flex items-center gap-1">
                  <ShoppingCart className="h-3 w-3 text-blue-500" /> Draft Sales ({draftCheck.draft_sales.length})
                </span>
                {draftCheck.draft_sales.map(s => (
                  <div key={`sale-${s.id}`} className="flex items-center justify-between bg-white rounded px-3 py-1.5 border text-xs" data-testid={`draft-sale-${s.id}`}>
                    <span>{s.reference_number || `Sale #${s.id}`} — {s.date}</span>
                    <span className="font-medium tabular-nums">{formatCurrency(s.lines_total)}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Wave 5A P6: draft resolution actions hidden for PSB historical sessions */}
            {!isReadOnly && (
            <div className="flex items-center gap-2 pt-2 border-t border-amber-200">
              <Button
                size="sm"
                onClick={handlePostAllAndCalculate}
                disabled={postingDrafts || calculating}
                data-testid="post-all-and-calculate-btn"
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {postingDrafts ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <CheckCircle className="h-3 w-3 mr-1" />}
                Post All & Calculate
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleExcludeAndCalculate}
                disabled={postingDrafts || calculating}
                data-testid="exclude-and-calculate-btn"
              >
                Exclude & Calculate
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { setShowDraftDialog(false); setDraftCheck(null); }}
                disabled={postingDrafts || calculating}
                data-testid="cancel-draft-dialog-btn"
              >
                Cancel
              </Button>
            </div>
            )}
          </div>
        </CardContent>
      )}

      {calculation && isAudit && (
        <CardContent>
          {/* Phase 3 Step 5 — Recipe expansion summary (only when flag was ON
              for this snapshot). Surfaces parent-suppressed and ingredient-
              expanded line counts plus the engine version so auditors know
              which math produced these numbers. */}
          {calculation.recipe_expansion_enabled && (
            <div
              className="mb-4 border-2 border-purple-300 bg-purple-50 rounded-lg p-3"
              data-testid="recipe-expansion-summary-panel"
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-purple-800">
                  Recipe Expansion Active
                </span>
                <span
                  className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-purple-200 text-purple-900"
                  data-testid="engine-version-badge"
                  title={`Internal version code: ${calculation.engine_version || 'phase3.recipe_v1'}`}
                >
                  {(() => {
                    const v = calculation.engine_version || 'phase3.recipe_v1';
                    if (v === 'phase3.recipe_v1') return 'Recipe engine v1';
                    if (v === 'phase3.measure_v2') return 'Measure engine v2';
                    return v;
                  })()}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                <div>
                  <div className="text-purple-600">Recipes loaded</div>
                  <div className="font-mono font-semibold text-purple-900" data-testid="rx-recipes-loaded">
                    {calculation.recipe_expansion_summary?.recipes_loaded ?? 0}
                  </div>
                </div>
                <div>
                  <div className="text-purple-600">Outputs suppressed</div>
                  <div className="font-mono font-semibold text-purple-900" data-testid="rx-outputs-expanded">
                    {calculation.recipe_expansion_summary?.output_products_expanded ?? 0}
                  </div>
                </div>
                <div>
                  <div className="text-purple-600">Ingredients consumed</div>
                  <div className="font-mono font-semibold text-purple-900" data-testid="rx-ingredients-consumed">
                    {calculation.recipe_expansion_summary?.ingredients_consumed ?? 0}
                  </div>
                </div>
                <div>
                  <div className="text-purple-600">Expanded ingredient PIDs</div>
                  <div className="font-mono text-[11px] text-purple-900 truncate" data-testid="rx-ingredient-pids">
                    {(calculation.recipe_expansion_summary?.expanded_ingredient_pids || []).join(', ') || '—'}
                  </div>
                </div>
              </div>
              <p className="text-[11px] text-purple-700 mt-2 leading-relaxed">
                Sales of recipe-output products were redirected to ingredient
                deductions. Parent products show <code>consumption=0</code> and
                <code> conversion_source=recipe_output_suppressed</code>;
                ingredients show <code>conversion_source=recipe_expanded</code>.
              </p>
            </div>
          )}
          {/* Calculation warnings (strict methodology, missing inputs, etc.)
              2026-10-28 — error-severity summaries always show in full; the
              long tail of per-product warnings (can be 90+ for one session)
              collapses behind a toggle so the summary banner that matters is
              what the auditor actually reads, not buried under a page-long
              wall of near-duplicate amber items. */}
          {(calculation.calculation_warnings || []).length > 0 && (() => {
            const allWarnings = calculation.calculation_warnings;
            const errorWarnings = allWarnings.filter(w => (w.severity || '').toLowerCase() === 'error');
            const otherWarnings = allWarnings.filter(w => (w.severity || '').toLowerCase() !== 'error');
            const VISIBLE_OTHER = 5;
            const shownOther = warningsExpanded ? otherWarnings : otherWarnings.slice(0, VISIBLE_OTHER);
            const hiddenCount = otherWarnings.length - shownOther.length;
            const renderWarning = (w, i) => {
              const isError = (w.severity || '').toLowerCase() === 'error';
              const cls = isError
                ? 'border-red-300 bg-red-50 text-red-800'
                : 'border-amber-300 bg-amber-50 text-amber-800';
              return (
                <div
                  key={`${w.code || 'warn'}-${i}`}
                  className={`border-2 rounded-lg p-3 flex items-start gap-2 ${cls}`}
                  data-testid={`calculation-warning-${w.code || i}`}
                >
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <p className="text-xs leading-relaxed">{w.message}</p>
                </div>
              );
            };
            return (
              <div className="mb-4 space-y-2" data-testid="calculation-warnings">
                {errorWarnings.map((w, i) => renderWarning(w, `err-${i}`))}
                {shownOther.map((w, i) => renderWarning(w, `other-${i}`))}
                {hiddenCount > 0 && (
                  <button
                    type="button"
                    data-testid="calculation-warnings-show-more-btn"
                    onClick={() => setWarningsExpanded(true)}
                    className="text-xs font-medium text-amber-700 hover:text-amber-900 underline px-3"
                  >
                    Show {hiddenCount} more item{hiddenCount === 1 ? '' : 's'}
                  </button>
                )}
                {warningsExpanded && otherWarnings.length > VISIBLE_OTHER && (
                  <button
                    type="button"
                    data-testid="calculation-warnings-show-less-btn"
                    onClick={() => setWarningsExpanded(false)}
                    className="text-xs font-medium text-amber-700 hover:text-amber-900 underline px-3"
                  >
                    Show fewer
                  </button>
                )}
              </div>
            );
          })()}
          {/* iter390 — Results Breakdown — Option A: Part 1 Stock Movement
              (At Cost) → Expected/Actual Closing. Part 2 Sales Reconciliation
              (Inc VAT) → Stocktake Result (Inc VAT) which matches the
              headline KPI tile. Every figure is a ClickableFigure so the
              auditor can drill in to its source. Replaces the prior single
              waterfall whose final row was mislabelled as the headline. */}
          {/* ── Results Breakdown ─────────────────────────────────────────────
              Ledger-style waterfall: Part 1 at cost, Part 2 Inc VAT.
              Green rows = additions · Red rows = deductions.
              Click any underlined figure to drill in.
              ──────────────────────────────────────────────────────────────── */}
          <div className="border rounded-lg overflow-hidden mb-4" data-testid="audit-results-breakdown">

            {/* Hint bar */}
            <div className="bg-slate-50 border-b px-4 py-2">
              <p className="text-[11px] text-slate-500">
                Click any underlined figure to see its detailed breakdown.
                <span className="ml-2 text-slate-400">Part 1 is at cost · Part 2 is inclusive of VAT.</span>
              </p>
            </div>

            {/* ══ PART 1 — Stock Movement (At Cost) ════════════════════════ */}
            <div className="px-4 py-2.5 bg-slate-100 border-b flex items-center gap-2.5" data-testid="rb-part1-heading">
              <span className="text-[9px] font-bold bg-slate-600 text-white rounded px-1.5 py-0.5 uppercase tracking-widest shrink-0">Part 1</span>
              <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Stock Movement — At Cost</span>
            </div>

            {/* Opening Stock — neutral starting balance */}
            <div className="flex justify-between items-center px-4 py-2.5 border-b border-slate-100">
              <span className="text-sm text-slate-700">
                <span className="inline-block w-4 mr-1.5 text-center text-slate-300 select-none font-bold">·</span>
                Opening Stock
              </span>
              <ClickableFigure type="opening" testId="audit-opening">
                <span className="tabular-nums font-medium text-slate-800">{formatCurrency(calculation.opening_stock_value)}</span>
              </ClickableFigure>
            </div>

            {/* Deliveries — ADDITION */}
            <div className="flex justify-between items-center px-4 py-2.5 border-b border-slate-100 bg-emerald-50/60">
              <span className="text-sm text-emerald-700 font-medium">
                <span className="inline-block w-4 mr-1.5 text-center text-emerald-500 font-bold select-none">+</span>
                Deliveries
              </span>
              <ClickableFigure type="deliveries" testId="audit-deliveries">
                {formatAccounting(calculation.deliveries_value)}
              </ClickableFigure>
            </div>

            {/* Credits — DEDUCTION */}
            <div className="flex justify-between items-center px-4 py-2.5 border-b border-slate-100 bg-red-50/60">
              <span className="text-sm text-red-600 font-medium">
                <span className="inline-block w-4 mr-1.5 text-center text-red-400 font-bold select-none">−</span>
                Credits
              </span>
              <ClickableFigure type="credits" testId="audit-credits">
                {formatAccounting(-(calculation.credits_value || 0))}
              </ClickableFigure>
            </div>

            {/* Cellar / Location Transfers */}
            {(isCellarSession || isLocationSession) && (
              <>
                <div className="flex justify-between items-center px-4 py-2.5 border-b border-slate-100 bg-emerald-50/60">
                  <span className="text-sm text-emerald-700 font-medium">
                    <span className="inline-block w-4 mr-1.5 text-center text-emerald-500 font-bold select-none">+</span>
                    {isCellarSession ? 'Stock Received from Locations' : hasCellar ? 'Stock Received (cellar and other locations)' : 'Stock Received from Other Locations'}
                  </span>
                  <ClickableFigure type="transfers_in" testId="audit-transfers-in">
                    {formatAccounting(calculation.transfers_in_value || 0)}
                  </ClickableFigure>
                </div>
                <div className="flex justify-between items-center px-4 py-2.5 border-b border-slate-100 bg-red-50/60">
                  <span className="text-sm text-red-600 font-medium">
                    <span className="inline-block w-4 mr-1.5 text-center text-red-400 font-bold select-none">−</span>
                    {isCellarSession ? 'Stock Issued to Locations' : hasCellar ? 'Stock Returned to Cellar or Sent Out' : 'Stock Sent to Other Locations'}
                  </span>
                  <ClickableFigure type="transfers_out" testId="audit-transfers-out">
                    {formatAccounting(-(calculation.transfers_out_value || 0))}
                  </ClickableFigure>
                </div>
              </>
            )}

            {/* Subtotal: Stock Available */}
            <div className="flex justify-between items-center px-4 py-2.5 bg-slate-100 border-b border-slate-300" data-testid="rb-stock-available">
              <span className="text-sm font-semibold text-slate-700">
                <span className="inline-block w-4 mr-1.5 text-center text-slate-500 select-none font-bold">=</span>
                Stock Available
              </span>
              <span className="tabular-nums font-semibold text-slate-800">
                {formatCurrency(
                  (calculation.opening_stock_value || 0)
                  + (calculation.deliveries_value || 0)
                  - (calculation.credits_value || 0)
                  + (calculation.transfers_in_value || 0)
                  - (calculation.transfers_out_value || 0)
                )}
              </span>
            </div>

            {/* Consumption / Cost of Sales — DEDUCTION
                Bug-3 fix: Expected Closing is always computed from PLU-based sales_at_cost in
                the engine (not cost_of_sales_indirect), so we always show sales_at_cost here
                to keep the Part 1 arithmetic consistent (Stock Available − COS − Mgmt = Expected Closing).
                The indirect COS (stock-movement basis) drives GP and is visible in the KPI tiles above. */}
            {!(isCellarSession || isLocationSession) && (
              <div className="flex justify-between items-center px-4 py-2.5 border-b border-slate-100 bg-red-50/60">
                <span className="text-sm text-red-600 font-medium">
                  <span className="inline-block w-4 mr-1.5 text-center text-red-400 font-bold select-none">−</span>
                  Cost of Sales
                  <span className="text-[10px] text-slate-400 font-normal ml-1.5">(PLU-derived)</span>
                </span>
                <ClickableFigure type="cos" testId="audit-cos">
                  {formatAccounting(-((calculation.sales_at_cost || calculation.sales_value) || 0))}
                </ClickableFigure>
              </div>
            )}

            {/* Management / Adjustments — neutral adjustment */}
            {(() => {
              const mgmt = calculation.management_value;
              const allow = calculation.allowance_value;
              const legacyAdj = calculation.adjustments_value || 0;
              const hasSplit = (mgmt != null) || (allow != null);
              return hasSplit ? (
                <div className="flex justify-between items-center px-4 py-2.5 border-b border-slate-100" data-testid="audit-management-row">
                  <span className="text-sm text-slate-500">
                    <span className="inline-block w-4 mr-1.5 text-center text-slate-400 select-none font-bold">±</span>
                    Management
                    <span className="text-[10px] text-slate-400 font-normal ml-1.5">(At Cost)</span>
                  </span>
                  <ClickableFigure type="management" testId="audit-management">
                    {formatAccounting(mgmt || 0)}
                  </ClickableFigure>
                </div>
              ) : (
                <div className="flex justify-between items-center px-4 py-2.5 border-b border-slate-100">
                  <span className="text-sm text-slate-500">
                    <span className="inline-block w-4 mr-1.5 text-center text-slate-400 select-none font-bold">±</span>
                    Adjustments
                    <span className="text-[10px] text-slate-400 font-normal ml-1.5">(At Cost)</span>
                  </span>
                  <ClickableFigure type="adjustments" testId="audit-adjustments">
                    {formatAccounting(legacyAdj)}
                  </ClickableFigure>
                </div>
              );
            })()}

            {/* Subtotal: Expected Closing Stock */}
            <div className="flex justify-between items-center px-4 py-2.5 bg-slate-100 border-b border-slate-300">
              <span className="text-sm font-semibold text-slate-700">
                <span className="inline-block w-4 mr-1.5 text-center text-slate-500 select-none font-bold">=</span>
                Expected Closing Stock
              </span>
              <ClickableFigure type="expected" testId="audit-expected">
                <span className="tabular-nums font-semibold">{formatCurrency(calculation.expected_closing_value)}</span>
              </ClickableFigure>
            </div>

            {/* Actual Closing — comparison row */}
            <div className="flex justify-between items-center px-4 py-2.5 border-b border-slate-200" data-testid="rb-actual-closing">
              <span className="text-sm text-slate-500 pl-6">
                Actual Closing counted
              </span>
              <ClickableFigure type="closing" testId="audit-actual-closing">
                <span className="tabular-nums text-slate-700">{formatCurrency(calculation.total_value)}</span>
              </ClickableFigure>
            </div>

            {/* ══ PART 2 — Sales Reconciliation (Inc VAT) ══════════════════ */}
            <div className="px-4 py-2.5 bg-slate-100 border-b border-t-2 border-t-slate-200 flex items-center gap-2.5" data-testid="rb-part2-heading">
              <span className="text-[9px] font-bold bg-slate-600 text-white rounded px-1.5 py-0.5 uppercase tracking-widest shrink-0">Part 2</span>
              <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Sales Reconciliation — Inc VAT</span>
            </div>

            {/* Part 2 rows — same calculation logic, new presentation */}
            {(() => {
              const actR = calculation.accounts_sales_incl_vat || 0;
              const grossEsr = calculation.estimated_receipts_incl_vat_gross != null
                ? Number(calculation.estimated_receipts_incl_vat_gross)
                : null;
              const mgmtRetailIncl = calculation.management_sales_incl_vat || 0;  // signed: + surplus, − add-back
              const mgmtAbs = Math.abs(mgmtRetailIncl);
              const allowIncl = calculation.allowance_value || 0;

              // Always use the stored Net ESR (correctly computed by the engine).
              // Net ESR = Gross − Allowances ONLY. Management PLU is a RESULT-STAGE
              // deduction, not an ESR component. See iter767 for the rationale.
              const netEsr = calculation.estimated_receipts_incl_vat != null
                ? Number(calculation.estimated_receipts_incl_vat)
                : grossEsr != null
                  ? +(grossEsr - allowIncl).toFixed(2)
                  : 0;

              // iter600 snapshots baked management into the stored Net ESR value;
              // detect this so we do not double-deduct it in the waterfall.
              const derivedNetOldFormula = grossEsr != null ? +(grossEsr - allowIncl).toFixed(2) : null;
              const mgmtBakedIntoEsr = derivedNetOldFormula != null && Math.abs(netEsr - derivedNetOldFormula) > 0.05;

              // iter767 — displayNetEsr is ALWAYS Gross − Allowances (no management
              // deduction). Management appears as its own "− Management PLU" row.
              const displayNetEsr = netEsr;

              const tradingShortfall = +(actR - displayNetEsr).toFixed(2);
              const wastageIncluded = calculation.wastage_included_value != null
                ? Number(calculation.wastage_included_value)
                : 0;
              const finalResult = calculation.stocktake_result_revised_adjusted != null
                ? Number(calculation.stocktake_result_revised_adjusted)
                : +(tradingShortfall - (!mgmtBakedIntoEsr ? mgmtRetailIncl : 0) + wastageIncluded).toFixed(2);

              // Verify arithmetic so we only render rows that are genuinely in the result.
              // Formula: finalResult ≈ tradingShortfall − mgmt + wastage
              const mgmtForArith = !mgmtBakedIntoEsr ? mgmtRetailIncl : 0;
              const _arithOk = Math.abs(finalResult - tradingShortfall + mgmtForArith - wastageIncluded) < 0.10;
              const hasMgmtInResult = Math.abs(mgmtForArith) > 0.005 && _arithOk;
              const hasWastageInResult = Math.abs(wastageIncluded) > 0.005 && _arithOk;

              // White-safe formatter for the dark final-result banner
              const fmtResultWhite = (val) => {
                const num = Number(val);
                const abs = Math.abs(num).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                return num < 0 ? `(£${abs})` : `£${abs}`;
              };

              const esrUnavailable = calculation.estimated_receipts_incl_vat == null
                && calculation.headline_fallback_reason === 'no_estimated_receipts';
              if (esrUnavailable) {
                return (
                  <div className="px-4 py-3 text-sm text-red-700 bg-red-50" data-testid="rb-esr-unavailable">
                    Actual Receipts {formatCurrency(actR)} — Net Estimated Receipts and the Stocktake Result are
                    unavailable because none of the consumption could be priced (no site selling prices).
                  </div>
                );
              }

              return (
                <>
                  {/* Actual Receipts — neutral starting value */}
                  <div className="flex justify-between items-center px-4 py-2.5 border-b border-slate-100">
                    <span className="text-sm text-slate-700">
                      <span className="inline-block w-4 mr-1.5 text-center text-slate-300 select-none font-bold">·</span>
                      Actual Receipts
                      <span className="text-[10px] text-slate-400 font-normal ml-1.5">(Inc VAT)</span>
                    </span>
                    <ClickableFigure type="actual_receipts" testId="audit-actual-receipts">
                      <span className="tabular-nums font-medium text-slate-800">{formatCurrency(actR)}</span>
                    </ClickableFigure>
                  </div>

                  {/* Net Estimated Receipts — DEDUCTION */}
                  <div className="flex justify-between items-center px-4 py-2.5 border-b border-slate-100 bg-red-50/60">
                    <span className="text-sm text-red-600 font-medium">
                      <span className="inline-block w-4 mr-1.5 text-center text-red-400 font-bold select-none">−</span>
                      Net Estimated Receipts
                      <span className="text-[10px] text-red-400 font-normal ml-1.5">(Inc VAT)</span>
                    </span>
                    <ClickableFigure type="estimated_receipts" testId="audit-estimated-receipts">
                      {formatAccounting(-displayNetEsr)}
                    </ClickableFigure>
                  </div>

                  {/* Trading Shortfall / Surplus — signed subtotal */}
                  <div
                    className={`flex justify-between items-center px-4 py-2.5 border-b font-semibold ${
                      tradingShortfall < 0
                        ? 'bg-red-50 border-red-200'
                        : tradingShortfall > 0
                          ? 'bg-emerald-50 border-emerald-200'
                          : 'bg-slate-100 border-slate-300'
                    }`}
                    data-testid="rb-raw-result"
                  >
                    <span className={`text-sm ${
                      tradingShortfall < 0 ? 'text-red-700' : tradingShortfall > 0 ? 'text-emerald-700' : 'text-slate-700'
                    }`}>
                      <span className="inline-block w-4 mr-1.5 text-center select-none font-bold">=</span>
                      Trading Shortfall / Surplus
                    </span>
                    <span className="tabular-nums">{formatAccounting(tradingShortfall)}</span>
                  </div>

                  {/* Wastage — ADDITION (only when arithmetically part of result) */}
                  {hasMgmtInResult && (
                    <div className="flex justify-between items-center px-4 py-2.5 border-b border-slate-100 bg-red-50/40">
                      <span className="text-sm text-red-600 font-medium">
                        <span className="inline-block w-4 mr-1.5 text-center text-red-400 font-bold select-none">{mgmtForArith > 0 ? '−' : '+'}</span>
                        Management PLU
                        <span className="text-[10px] text-red-400 font-normal ml-1.5">{mgmtForArith > 0 ? '(surplus taken in Actual Receipts)' : '(add-back)'}</span>
                      </span>
                      <ClickableFigure type="result" testId="audit-mgmt-plu-deduction">
                        {formatAccounting(-mgmtForArith)}
                      </ClickableFigure>
                    </div>
                  )}

                  {/* Wastage — ADDITION (only when arithmetically part of result) */}
                  {hasWastageInResult && (
                    <div className="flex justify-between items-center px-4 py-2.5 border-b border-slate-100 bg-emerald-50/60">
                      <span className="text-sm text-emerald-700 font-medium">
                        <span className="inline-block w-4 mr-1.5 text-center text-emerald-500 font-bold select-none">+</span>
                        Wastage
                        <span className="text-[10px] text-slate-400 font-normal ml-1.5">(included in result)</span>
                      </span>
                      <ClickableFigure type="allowance" testId="audit-wastage">
                        {formatAccounting(wastageIncluded)}
                      </ClickableFigure>
                    </div>
                  )}

                  {/* Stocktake Result — full-width colour-coded banner */}
                  <div
                    className={`flex justify-between items-center px-4 py-3 ${
                      finalResult < 0 ? 'bg-red-600' : finalResult > 0 ? 'bg-emerald-600' : 'bg-slate-500'
                    }`}
                    data-testid="rb-stocktake-result"
                  >
                    <span className="text-sm font-bold text-white">
                      <span className="inline-block w-4 mr-1.5 text-center select-none font-bold">=</span>
                      Stocktake Result
                      <span className="font-normal text-white/70 text-[10px] ml-1.5">(Inc VAT)</span>
                    </span>
                    <ClickableFigure type="result" testId="audit-result">
                      <span className="tabular-nums font-bold text-white text-base">
                        {fmtResultWhite(finalResult)}
                      </span>
                    </ClickableFigure>
                  </div>
                </>
              );
            })()}
          </div>

          {/* iter340 v8 — Headline + KPIs consolidated into a single
              "Key Performance Indicators" section. Three logical rows:
                1. Receipts comparison: Actual Receipts · Estimated · Result
                2. Cost / margin:       COS · COS% · GP · GP%
                3. Operational:         Yield% · Stockholding · Closing Stock
              Variance % was removed in iter340 v8 because it's the same
              figure already rendered as the Result tile's percentage.
              Every tile is clickable and opens its existing drilldown. */}
          {(() => {
            const methodology = calculation.headline_methodology || 'legacy';
            const isRevised = methodology === 'revised';
            const fallbackReason = calculation.headline_fallback_reason;

            // Headline result figure (allowance/mgmt-adjusted in iter340 v3+).
            const adjustedRevised = calculation.stocktake_result_revised_adjusted ?? calculation.stocktake_result_revised;
            const adjustedLegacy = calculation.stocktake_result_legacy_adjusted ?? (calculation.stocktake_result_legacy ?? calculation.stocktake_result);
            const headlineResultRaw = isRevised
              ? (calculation.stocktake_result_revised_adjusted != null
                  ? calculation.stocktake_result_revised_adjusted
                  : calculation.headline_stocktake_result)
              : adjustedLegacy;
            const headlineBlocked = isRevised && (headlineResultRaw == null);
            const headlineResult = headlineResultRaw ?? adjustedLegacy ?? 0;
            const arBase = calculation.accounts_sales_incl_vat ?? calculation.accounts_sales_excl_vat;
            const headlinePct = (arBase && arBase > 0) ? ((headlineResult || 0) / arBase) * 100 : null;
            const headlineLabel = headlineResult > 0 ? 'Surplus' : headlineResult < 0 ? 'Shortage' : 'Balanced';

            // Methodology-aware sources for COS / GP figures.
            // 2026-02-16 — Global GP overhaul: GP and COS tiles ALWAYS use
            // `cost_of_sales_indirect` (Consumption at Cost) and Actual Receipts
            // (excl VAT) when both are available, regardless of org methodology.
            // Falls back to legacy PLU values only when accounts_sales is absent
            // (e.g. cellar sessions).
            const hasActualReceipts = (calculation.accounts_sales_excl_vat != null)
              && (calculation.cost_of_sales_indirect != null);
            const cosForKpi = hasActualReceipts
              ? calculation.cost_of_sales_indirect
              : ((sessionKpis?.sales_at_cost) || calculation.sales_at_cost || 0);
            const revBase = hasActualReceipts
              ? (calculation.accounts_sales_excl_vat || 0)
              : ((sessionKpis?.sales_revenue) || calculation.sales_at_selling_excl_vat || 0);
            const cosPctVal = revBase > 0 ? (cosForKpi / revBase) * 100 : 0;
            const gpVal = revBase - cosForKpi;
            const gpPctVal = revBase > 0 ? (gpVal / revBase) * 100 : 0;

            // iter340 v6 — recompute period_days + stockholding_days
            // CLIENT-SIDE so old snapshots (stored under the +1 / decimal
            // formula) display the corrected whole-day figure without the
            // user having to hit Recalculate. Falls back to the snapshot
            // value when raw inputs aren't available.
            const pf = calculation.period_from || calculation.session_period_from;
            const pt = calculation.period_to || calculation.session_period_to;
            let livePeriodDays = null;
            if (pf && pt) {
              const d1 = new Date(pf);
              const d2 = new Date(pt);
              if (!isNaN(d1) && !isNaN(d2)) {
                livePeriodDays = Math.max(1, Math.round((d2 - d1) / 86_400_000));
              }
            }
            let liveStockholdingDays = null;
            const closingForDays = calculation.total_value || 0;
            if (livePeriodDays && cosForKpi > 0 && closingForDays > 0) {
              const avgDaily = cosForKpi / livePeriodDays;
              if (avgDaily > 0) liveStockholdingDays = Math.round(closingForDays / avgDaily);
            }
            const stockholdingDaysFinal = liveStockholdingDays != null
              ? liveStockholdingDays
              : (calculation.stockholding_days != null
                  ? Math.round(Number(calculation.stockholding_days))
                  : null);

            const yieldPct = sessionKpis?.kpis?.yield_pct ?? null;
            // iter340 v8 — methodology label now hides the word
            // "Revised". The "Actual Receipts − Estimated Receipts +
            // Allowances − Mgmt Retail" formula is the official method
            // going forward; legacy is shown only when explicitly used.
            const methodLabel = isRevised ? 'Actual Receipts methodology' : 'Legacy (PLU)';
            const costMethod = sessionKpis?.cost_method === 'weighted_average' ? 'WAC' : (sessionKpis?.cost_method || '—');
            const tradingDays = livePeriodDays ?? (sessionKpis?.trading_days ?? null);

            const Tile = ({ label, type, value, sub, color, big = false, highlight = null, testId }) => (
              <button
                type="button"
                onClick={() => setBreakdownType(type)}
                className={`rounded-lg border px-3 py-2 text-left transition-all focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-slate-400 hover:shadow-sm ${
                  highlight === 'surplus' ? 'border-emerald-300 bg-emerald-50/70 hover:bg-emerald-50'
                  : highlight === 'shortage' ? 'border-red-300 bg-red-50/70 hover:bg-red-50'
                  : highlight === 'blocked' ? 'border-red-300 bg-red-50/70'
                  : 'bg-white hover:border-slate-400'
                }`}
                data-testid={testId}
              >
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
                <p className={`tabular-nums font-bold ${big ? 'text-xl' : 'text-lg'} ${color || 'text-slate-900'}`}>{value}</p>
                {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
              </button>
            );

            return (
              <div className="space-y-3" data-testid="wizard-kpi-panel">
                <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <Activity className="h-4 w-4 text-indigo-600" />
                  Key Performance Indicators
                  <span className="text-[10px] text-muted-foreground">
                    ({methodLabel} • {costMethod}{tradingDays ? ` • ${tradingDays} days` : ''})
                  </span>
                  <Badge
                    variant="outline"
                    className={`text-[10px] font-medium ml-auto ${isRevised ? 'border-emerald-400 bg-emerald-50 text-emerald-700' : 'border-slate-300 bg-slate-50 text-slate-600'}`}
                    data-testid="headline-methodology-badge"
                  >
                    {isRevised ? 'Actual − Net ESR (Gross − Allow)' : 'Legacy — PLU-led'}
                  </Badge>
                  {fallbackReason && (
                    <Badge variant="outline" className="text-[10px] border-amber-300 bg-amber-50 text-amber-700" title={`Methodology not applied: ${fallbackReason.replace(/_/g, ' ')}`} data-testid="headline-fallback-badge">
                      fallback
                    </Badge>
                  )}
                </div>

                {/* Row 1 — Receipts comparison + headline Result. */}
                <div className="grid grid-cols-3 gap-2">
                  <Tile
                    label="Actual Receipts (incl VAT)"
                    type="accounts_sales"
                    value={(calculation.accounts_sales_incl_vat ?? calculation.accounts_sales_excl_vat) != null
                      ? formatCurrency(calculation.accounts_sales_incl_vat ?? calculation.accounts_sales_excl_vat)
                      : '—'}
                    sub="Till receipts entered on Sales step"
                    testId="kpi-actual-receipts"
                  />
                  <Tile
                    label="Estimated Receipts (incl VAT)"
                    type="estimated_receipts"
                    value={deriveNetEsrIncl(calculation) != null
                      ? formatCurrency(deriveNetEsrIncl(calculation))
                      : '—'}
                    sub="Σ consumption × selling price, net of Allowances"
                    testId="kpi-estimated-receipts"
                  />
                  <Tile
                    label="Result"
                    type="result"
                    big
                    highlight={headlineBlocked ? 'blocked' : (headlineResult > 0 ? 'surplus' : headlineResult < 0 ? 'shortage' : null)}
                    color={headlineBlocked ? 'text-red-700' : headlineResult > 0 ? 'text-emerald-700' : headlineResult < 0 ? 'text-red-700' : 'text-slate-900'}
                    value={headlineBlocked ? '—' : formatAccounting(headlineResult)}
                    sub={headlineBlocked
                      ? (fallbackReason === 'no_estimated_receipts'
                          ? 'Unavailable — Estimated Receipts could not be priced'
                          : 'Unavailable — Actual Receipts required')
                      : (headlinePct == null
                          ? `${headlineLabel} · Result % = Result ÷ Actual Receipts (incl VAT) × 100`
                          : `${headlineLabel} · ${headlinePct.toFixed(2)}% (Result ÷ Actual Receipts incl VAT)`)}
                    testId="audit-result"
                  />
                </div>

                {/* Row 2 — Cost / margin. Single source of truth.
                    2026-02-16 — Added "Consumption at Cost" KPI; GP now
                    universally = Actual Receipts − Consumption at Cost. */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  <Tile
                    label="Consumption at Cost"
                    type="consumption_at_cost"
                    value={calculation.cost_of_sales_indirect != null
                      ? formatCurrency(calculation.cost_of_sales_indirect)
                      : '—'}
                    sub="consumption qty × WAC cost/unit"
                    testId="kpi-consumption-at-cost"
                  />
                  <Tile
                    label="Cost of Sales"
                    type={hasActualReceipts ? 'cos_indirect' : 'cos'}
                    value={formatCurrency(cosForKpi)}
                    sub={hasActualReceipts
                      ? `Consumption at Cost · Part 1 PLU-derived ${formatCurrency(calculation.sales_at_cost || 0)}; gap = stock variance at cost`
                      : 'PLU-led (no Actual Receipts)'}
                    testId="kpi-cos"
                  />
                  <Tile
                    label="COS %"
                    type="cos_pct"
                    color={cosPctVal > 80 ? 'text-red-600' : cosPctVal > 60 ? 'text-amber-600' : 'text-emerald-600'}
                    value={`${roundHalfUp(cosPctVal || 0, 1).toFixed(1)}%`}
                    sub={`${formatCurrency(cosForKpi)} / ${formatCurrency(revBase)}`}
                    testId="kpi-cos-%"
                  />
                  <Tile
                    label="Gross Profit"
                    type="gp"
                    value={formatCurrency(gpVal)}
                    sub={hasActualReceipts ? 'Actual Receipts excl VAT − Consumption at Cost' : 'PLU revenue − COS'}
                    testId="kpi-gp"
                  />
                  <Tile
                    label="GP %"
                    type="gp_pct"
                    color={gpPctVal < 20 ? 'text-red-600' : gpPctVal < 40 ? 'text-amber-600' : 'text-emerald-600'}
                    value={`${roundHalfUp(gpPctVal || 0, 1).toFixed(1)}%`}
                    sub={`GP: ${formatCurrency(gpVal)}`}
                    testId="kpi-gp-%"
                  />
                </div>

                {/* Row 3 — Operational benchmarks. */}
                <div className="grid grid-cols-3 gap-2">
                  <Tile
                    label="Yield %"
                    type="yield_pct"
                    color={Math.abs((yieldPct || 0) - 100) <= 2 ? 'text-emerald-600' : Math.abs((yieldPct || 0) - 100) <= 10 ? 'text-amber-600' : 'text-red-600'}
                    value={analyticsLoading
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin inline text-slate-400" />
                      : (yieldPct != null ? `${roundHalfUp(yieldPct, 1).toFixed(1)}%` : '—')}
                    sub={sessionKpis?.kpis?.yield_pct_note
                      ? `— ${sessionKpis.kpis.yield_pct_note}`
                      : 'Actual ÷ (Gross ESR + Management, signed) — before allowances'}
                    testId="kpi-yield-%"
                  />
                  <Tile
                    label="Stockholding"
                    type="stockholding"
                    color="text-blue-600"
                    value={stockholdingDaysFinal != null ? `${stockholdingDaysFinal} days` : '—'}
                    sub={`Closing ÷ Avg daily COS${livePeriodDays ? ` · ${livePeriodDays}-day period` : ''}`}
                    testId="kpi-stockholding"
                  />
                  <Tile
                    label="Closing Stock"
                    type="actual"
                    color="text-blue-600"
                    value={calculation.total_value != null ? formatCurrency(calculation.total_value) : '—'}
                    sub="Counted closing stock value (excl VAT)"
                    testId="kpi-closing-stock"
                  />
                </div>
              </div>
            );
          })()}

          {/* Receipts Reconciliation — only shown when org is on LEGACY
              methodology as a "preview" of what the revised switch would
              yield. When the org is already on revised, this is duplicated
              by the headline Actual Receipts / Estimated Receipts / Surplus
              cards so we hide it to de-clutter the page. */}
          {!isCellarSession && ((calculation.headline_methodology || 'legacy') !== 'revised') && (
            <div className="rounded-lg border-2 border-indigo-200 bg-indigo-50/40 p-4 mb-4" data-testid="receipts-reconciliation">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-indigo-900 font-semibold text-sm">
                  Receipts Reconciliation (preview)
                </span>
                <span className="text-[10px] bg-white border border-indigo-200 text-indigo-700 px-1.5 py-0.5 rounded">Preview — not yet headline</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="p-3 bg-white rounded-md border border-indigo-100">
                  <p className="text-xs text-muted-foreground">Actual Receipts (Actual Receipts)</p>
                  <p className="text-lg font-bold text-slate-900 tabular-nums" data-testid="recon-accounts-sales">
                    {(calculation.accounts_sales_incl_vat ?? calculation.accounts_sales_excl_vat) != null ? formatCurrency(calculation.accounts_sales_incl_vat ?? calculation.accounts_sales_excl_vat) : <span className="text-slate-400 text-sm">Not entered</span>}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">Incl. VAT — entered on Sales step</p>
                </div>
                <div className="p-3 bg-white rounded-md border border-indigo-100">
                  <p className="text-xs text-muted-foreground">Estimated Receipts</p>
                  <p className="text-lg font-bold text-slate-900 tabular-nums" data-testid="recon-estimated-receipts">
                    {deriveNetEsrIncl(calculation) != null ? formatCurrency(deriveNetEsrIncl(calculation)) : <span className="text-slate-400 text-sm">—</span>}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">Σ consumption × selling price (incl VAT), net of Allowances</p>
                </div>
                {(() => {
                  const _actR = calculation.accounts_sales_incl_vat ?? calculation.accounts_sales_excl_vat;
                  const _net = deriveNetEsrIncl(calculation);
                  const _diff = (_actR != null && _net != null) ? +(_actR - _net).toFixed(2) : null;
                  const _surplus = _diff != null && _diff >= 0;
                  return (
                <div className={`p-3 rounded-md border-2 ${_diff == null ? 'bg-white border-slate-200' : _surplus ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}>
                  <p className="text-xs text-muted-foreground">Difference</p>
                  <p className={`text-lg font-bold tabular-nums ${_diff == null ? 'text-slate-400' : _surplus ? 'text-green-700' : 'text-red-700'}`} data-testid="recon-difference">
                    {_diff != null ? formatAccounting(_diff) : '—'}
                  </p>
                  <p className={`text-[10px] font-medium mt-0.5 ${_diff == null ? 'text-slate-500' : _surplus ? 'text-green-700' : 'text-red-700'}`} data-testid="recon-status">
                    {_diff == null
                      ? 'Enter Actual Receipts on the Sales step to compare'
                      : _surplus
                        ? 'Surplus — actual receipts exceed stock-derived estimate'
                        : 'Shortage — stock-derived estimate exceeds actual receipts'}
                  </p>
                </div>
                  );
                })()}
              </div>
            </div>
          )}
          {isCellarSession && (calculation.estimated_receipts_excl_vat == null) && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 mb-4 text-xs text-slate-600" data-testid="receipts-reconciliation-cellar">
              Receipts Reconciliation is not applicable for cellar sessions — cellar movement represents transfers to outlets, not retail receipts.
            </div>
          )}

          {/* Inline Validation Report */}
          {localValidation && (
            <div className={`rounded-lg border p-3 mb-4 ${localValidation.valid ? 'border-emerald-200 bg-emerald-50/50' : 'border-amber-200 bg-amber-50/50'}`} data-testid="inline-validation-summary">
              <div className="flex items-center gap-2">
                {localValidation.valid ? (
                  <>
                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                    <span className="text-sm font-medium text-emerald-700">All verification checks passed</span>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                    <span className="text-sm font-medium">
                      {localValidation.error_count > 0 && <span className="text-red-600">{localValidation.error_count} error(s)</span>}
                      {localValidation.error_count > 0 && localValidation.warning_count > 0 && ', '}
                      {localValidation.warning_count > 0 && <span className="text-amber-600">{localValidation.warning_count} warning(s)</span>}
                    </span>
                  </>
                )}
              </div>
              {!localValidation.valid && (
                <div className="space-y-1 mt-2 text-xs">
                  {(localValidation.errors || []).map((issue, idx) => (
                    <div key={`e-${idx}`} className="flex items-start gap-1.5 text-red-700">
                      <span className="h-1.5 w-1.5 rounded-full bg-red-500 mt-1.5 shrink-0" />
                      <span>{issue.message}</span>
                    </div>
                  ))}
                  {(localValidation.warnings || []).map((issue, idx) => (
                    <div key={`w-${idx}`} className="flex items-start gap-1.5 text-amber-700">
                      <span className="h-1.5 w-1.5 rounded-full bg-amber-500 mt-1.5 shrink-0" />
                      <span>{issue.message}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}


          {/* Stock Audit Analytics (Movement Trail) — Collapsible */}
          {costingReport && (
            <Collapsible open={movementTrailOpen} onOpenChange={setMovementTrailOpen}>
              <CollapsibleTrigger asChild>
                <button className="flex items-center gap-2 w-full py-2 mb-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors" data-testid="toggle-movement-trail-btn">
                  <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${movementTrailOpen ? 'rotate-90' : ''}`} />
                  Stock Audit Analytics ({costingReport.product_count || 0} products)
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="space-y-3">
                  {/* Informational: why this count can differ from Product Analytics */}
                  {costingReport.product_count && (calculation?.line_count || 0) > 0 && costingReport.product_count !== calculation.line_count && (
                    <div className="text-xs bg-slate-50 border rounded px-3 py-2 text-slate-600" data-testid="analytics-count-hint">
                      <span className="font-semibold">Why {costingReport.product_count} here vs {calculation.line_count} in Product Analytics?</span>{' '}
                      Stock Audit Analytics shows every product with <em>any</em> stock movement in the period
                      (opening stock, deliveries, credits, sales, adjustments, transfers).
                      Product Analytics only shows products that were <em>counted</em> at closing.
                      The difference ({costingReport.product_count - (calculation.line_count || 0)} product{(costingReport.product_count - (calculation.line_count || 0)) === 1 ? '' : 's'})
                      had movement but no count entry — typically ran to zero or were missed in the physical count.
                    </div>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                    <div className="rounded-lg border bg-white px-3 py-2">
                      <p className="text-[10px] text-muted-foreground">Opening Value</p>
                      <p className="text-base font-bold tabular-nums">{formatCurrency(costingReport.grand_opening_value)}</p>
                    </div>
                    <div className="rounded-lg border bg-white px-3 py-2">
                      <p className="text-[10px] text-emerald-600">Total In</p>
                      <p className="text-base font-bold tabular-nums text-emerald-700">{formatCurrency(costingReport.grand_in_value)}</p>
                    </div>
                    <div className="rounded-lg border bg-white px-3 py-2">
                      <p className="text-[10px] text-red-600">Total Out</p>
                      <p className="text-base font-bold tabular-nums text-red-700">{formatCurrency(costingReport.grand_out_value)}</p>
                    </div>
                    <div className="rounded-lg border bg-white px-3 py-2">
                      <p className="text-[10px] text-muted-foreground">Expected Closing</p>
                      <p className="text-base font-bold tabular-nums">{formatCurrency(costingReport.grand_closing_value)}</p>
                    </div>
                    {costingReport.has_audit_data && (
                      <>
                        <div className="rounded-lg border bg-white px-3 py-2">
                          <p className="text-[10px] text-indigo-600">Counted Closing</p>
                          <p className="text-base font-bold tabular-nums text-indigo-700">{formatCurrency(costingReport.grand_counted_value)}</p>
                        </div>
                        <div className="rounded-lg border bg-white px-3 py-2">
                          <p className="text-[10px] text-muted-foreground">Variance</p>
                          <p className={`text-base font-bold tabular-nums ${(costingReport.grand_variance_value || 0) < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                            {formatCurrency(costingReport.grand_variance_value)}
                          </p>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Search + category / sub-category filters (same methodology as wizard) */}
                  <div className="space-y-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input value={movementSearch} onChange={e => setMovementSearch(e.target.value)}
                        placeholder="Search by product name or code..."
                        className="pl-9 h-9 text-sm" data-testid="movement-search" />
                    </div>
                    {(movementCategories.length > 0 || movementSubcategories.length > 0) && (
                      <div className="space-y-2 p-2 bg-slate-50 rounded-lg border border-slate-100">
                        {movementCategories.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-slate-500 mb-1">Category</p>
                            <div className="flex flex-wrap gap-1.5">
                              {movementCategories.map(cat => {
                                const isActive = movementSelectedCats.includes(cat);
                                return (
                                  <button key={cat}
                                    onClick={() => setMovementSelectedCats(prev => isActive ? prev.filter(c => c !== cat) : [...prev, cat])}
                                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                                      isActive ? 'bg-rose-600 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
                                    }`} data-testid={`movement-cat-${cat}`}>{String(cat).replace(/_/g, ' ')}</button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {movementSubcategories.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-slate-500 mb-1">Sub-category</p>
                            <div className="flex flex-wrap gap-1.5">
                              {movementSubcategories.map(sub => {
                                const isActive = movementSelectedSubs.includes(sub);
                                return (
                                  <button key={sub}
                                    onClick={() => setMovementSelectedSubs(prev => isActive ? prev.filter(s => s !== sub) : [...prev, sub])}
                                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                                      isActive ? 'bg-rose-600 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
                                    }`} data-testid={`movement-subcat-${sub}`}>{sub}</button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {movementActiveFilters > 0 && (
                          <button onClick={() => { setMovementSelectedCats([]); setMovementSelectedSubs([]); }}
                            className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1" data-testid="movement-clear-filters">
                            <X className="w-3 h-3" /> Clear filters ({movementActiveFilters})
                          </button>
                        )}
                      </div>
                    )}
                    {(movementSearch || movementActiveFilters > 0) && (
                      <p className="text-[11px] text-slate-500" data-testid="movement-filter-count">
                        Showing {filteredMovementProducts.length} of {(costingReport?.products || []).length} products
                      </p>
                    )}
                  </div>

                  {/* Product Movement Table — Column Headers */}
                  <div className="flex items-center gap-2 px-3 py-1 text-[10px] font-medium text-muted-foreground border-b mb-1">
                    <span className="w-5" />
                    <span className="flex-1">Product</span>
                    <span className="tabular-nums w-16 text-right">Opening</span>
                    <span className="tabular-nums w-16 text-right">+ In</span>
                    <span className="tabular-nums w-16 text-right">- Out</span>
                    <span className="tabular-nums w-16 text-right">Expected</span>
                    {costingReport.has_audit_data && (
                      <span className="tabular-nums w-16 text-right">Variance</span>
                    )}
                  </div>

                  {/* Product Movement Rows */}
                  <div className="space-y-1">
                    {filteredMovementProducts.length === 0 && (
                      <p className="text-xs text-slate-500 py-3 text-center" data-testid="movement-empty">
                        {(movementSearch || movementActiveFilters > 0) ? 'No products match your filters' : 'No product movements'}
                      </p>
                    )}
                    {filteredMovementProducts.map(p => (
                      <Collapsible key={p.product_id}>
                        <CollapsibleTrigger asChild>
                          <div className="flex items-center gap-2 px-3 py-1.5 rounded border hover:bg-slate-50 cursor-pointer text-xs">
                            <ChevronRight className="h-3 w-3 text-muted-foreground" />
                            <span className="font-medium flex-1 flex items-center gap-1">
                              {p.product_name}
                              {p.is_post_mix && (
                                <span className="inline-flex items-center text-[9px] font-semibold bg-amber-100 text-amber-700 border border-amber-200 px-1 py-px rounded ml-0.5" title="Postmix concentrate — qty in stock units (concentrate), servings shown in movements">
                                  Postmix
                                </span>
                              )}
                            </span>
                            <span className="tabular-nums text-muted-foreground w-16 text-right">{formatCurrency(p.opening_value)}</span>
                            <span className="tabular-nums text-emerald-600 w-16 text-right">+{formatCurrency(p.total_in_value)}</span>
                            <span className="tabular-nums text-red-600 w-16 text-right">-{formatCurrency(p.total_out_value)}</span>
                            <span className="tabular-nums font-medium w-16 text-right">{formatCurrency(p.closing_value)}</span>
                            {costingReport.has_audit_data && (
                              <span className={`tabular-nums font-medium w-16 text-right ${p.variance_value != null && p.variance_value < 0 ? 'text-red-600' : p.variance_value != null ? 'text-emerald-600' : 'text-slate-400'}`}>
                                {p.variance_value != null ? formatCurrency(p.variance_value) : '-'}
                                {/* Show serving equivalent for postmix variance */}
                                {p.is_post_mix && p.post_mix_servings_per_unit && p.variance_qty != null && (
                                  <span className="block text-[9px] text-slate-400 font-normal">
                                    ≈{(p.variance_qty * p.post_mix_servings_per_unit).toFixed(1)} srv
                                  </span>
                                )}
                              </span>
                            )}
                          </div>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="ml-6 mr-2 mb-2 border rounded bg-slate-50/50 overflow-x-auto">
                            <table className="w-full text-[11px]">
                              <thead>
                                <tr className="border-b bg-slate-100">
                                  <th className="px-2 py-1 text-left">Type</th>
                                  <th className="px-2 py-1 text-left">Date</th>
                                  <th className="px-2 py-1 text-left">Reference</th>
                                  <th className="px-2 py-1 text-right">Qty In</th>
                                  <th className="px-2 py-1 text-right">Qty Out</th>
                                  <th className="px-2 py-1 text-right">Unit Cost</th>
                                  <th className="px-2 py-1 text-right">Value</th>
                                  <th className="px-2 py-1 text-right">Running Qty</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(p.movements || []).map((m, mi) => {
                                  const isClosing = m.txn_type === 'Expected Closing' || m.txn_type === 'Counted Closing';
                                  return (
                                    <tr key={mi} className={`border-b last:border-0 ${isClosing ? 'bg-slate-100 font-medium' : ''}`}>
                                      <td className="px-2 py-0.5">{m.txn_type}</td>
                                      <td className="px-2 py-0.5">{m.date}</td>
                                      <td className="px-2 py-0.5">{m.reference || '-'}</td>
                                      <td className="px-2 py-0.5 text-right tabular-nums text-emerald-600">{m.qty_in != null ? m.qty_in.toFixed(2) : ''}</td>
                                      <td className="px-2 py-0.5 text-right tabular-nums text-red-600">{m.qty_out != null ? m.qty_out.toFixed(2) : ''}</td>
                                      <td className="px-2 py-0.5 text-right tabular-nums">{m.unit_cost_in != null ? formatCurrency(m.unit_cost_in) : ''}</td>
                                      <td className="px-2 py-0.5 text-right tabular-nums">{m.value != null ? formatCurrency(m.value) : ''}</td>
                                      <td className="px-2 py-0.5 text-right tabular-nums text-slate-500">{m.running_qty != null ? m.running_qty.toFixed(2) : ''}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    ))}
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}

          {/* Product Analytics — Collapsible */}
          {calculation.lines && calculation.lines.length > 0 && (
            <Collapsible open={analyticsOpen} onOpenChange={setAnalyticsOpen}>
              <CollapsibleTrigger asChild>
                <button className="flex items-center gap-2 w-full py-2 mb-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors" data-testid="toggle-analytics-btn">
                  <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${analyticsOpen ? 'rotate-90' : ''}`} />
                  Product Analytics ({calculation.lines.length} items)
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
            {/* Search + category / sub-category filters (same methodology as wizard) */}
            <div className="space-y-2 mb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input value={productSearch} onChange={e => setProductSearch(e.target.value)}
                  placeholder="Search by product name or code..."
                  className="pl-9 h-9 text-sm" data-testid="product-analytics-search" />
              </div>
              {(productLineCategories.length > 0 || productLineSubcategories.length > 0) && (
                <div className="space-y-2 p-2 bg-slate-50 rounded-lg border border-slate-100">
                  {productLineCategories.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-slate-500 mb-1">Category</p>
                      <div className="flex flex-wrap gap-1.5">
                        {productLineCategories.map(cat => {
                          const isActive = productSelectedCats.includes(cat);
                          return (
                            <button key={cat}
                              onClick={() => setProductSelectedCats(prev => isActive ? prev.filter(c => c !== cat) : [...prev, cat])}
                              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                                isActive ? 'bg-rose-600 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
                              }`} data-testid={`product-analytics-cat-${cat}`}>{String(cat).replace(/_/g, ' ')}</button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {productLineSubcategories.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-slate-500 mb-1">Sub-category</p>
                      <div className="flex flex-wrap gap-1.5">
                        {productLineSubcategories.map(sub => {
                          const isActive = productSelectedSubs.includes(sub);
                          return (
                            <button key={sub}
                              onClick={() => setProductSelectedSubs(prev => isActive ? prev.filter(s => s !== sub) : [...prev, sub])}
                              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                                isActive ? 'bg-rose-600 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
                              }`} data-testid={`product-analytics-subcat-${sub}`}>{sub}</button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {productActiveFilters > 0 && (
                    <button onClick={() => { setProductSelectedCats([]); setProductSelectedSubs([]); }}
                      className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1" data-testid="product-analytics-clear-filters">
                      <X className="w-3 h-3" /> Clear filters ({productActiveFilters})
                    </button>
                  )}
                </div>
              )}
              {(productSearch || productActiveFilters > 0) && (
                <p className="text-[11px] text-slate-500" data-testid="product-analytics-filter-count">
                  Showing {filteredCalculationLines.length} of {calculation.lines.length} products
                </p>
              )}
            </div>
            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="audit-lines-table">
                  <thead>
                    <tr className="bg-slate-50 border-b text-xs text-muted-foreground">
                      <th className="text-left px-3 py-2 font-medium">Item Description</th>
                      <th className="text-right px-3 py-2 font-medium">Book Qty</th>
                      <th className="text-right px-3 py-2 font-medium">Closing Counted Qty</th>
                      <th className="text-right px-3 py-2 font-medium">Variance Qty</th>
                      <th className="text-right px-3 py-2 font-medium">Cost Price / Unit</th>
                      <th className="text-right px-3 py-2 font-medium">Book Cost</th>
                      <th className="text-right px-3 py-2 font-medium">Closing Counted Cost</th>
                      <th className="text-right px-3 py-2 font-medium">Variance Cost</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredCalculationLines.length === 0 && (
                      <tr data-testid="product-analytics-empty"><td colSpan={8} className="px-3 py-4 text-xs text-slate-500 text-center">No products match your filters</td></tr>
                    )}
                    {filteredCalculationLines.map((ln, idx) => {
                      const noCost = ln.cost_source === 'no_cost';
                      return (
                        <tr key={idx} className={`hover:bg-slate-50/50 ${noCost ? 'bg-amber-50/50' : ''}`} data-testid={`audit-line-${idx}`}>
                          <td className="px-3 py-2 font-medium">
                            {ln.product_name}
                            {noCost && <span className="ml-1.5 text-[10px] text-amber-600 font-normal">(no cost data)</span>}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums" data-testid={`book-qty-${idx}`}>
                            {Number(ln.expected_qty || 0).toLocaleString('en-GB', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                            {ln.base_unit && <span className="text-xs text-muted-foreground ml-1">{ln.base_unit}</span>}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums" data-testid={`closing-qty-${idx}`}>
                            {Number(ln.base_quantity || 0).toLocaleString('en-GB', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                            {ln.base_unit && <span className="text-xs text-muted-foreground ml-1">{ln.base_unit}</span>}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums" data-testid={`var-qty-${idx}`}>
                            {ln.variance_qty != null ? formatQtyAccounting(ln.variance_qty) : '-'}
                          </td>
                          <td className={`px-3 py-2 text-right tabular-nums ${noCost ? 'text-amber-600' : ''}`}>
                            {noCost ? 'N/A' : formatCurrency(ln.cost_per_base_unit)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums" data-testid={`book-cost-${idx}`}>
                            {noCost ? '\u00A30.00' : formatCurrency(ln.expected_value)}
                          </td>
                          <td className={`px-3 py-2 text-right tabular-nums font-medium ${noCost ? 'text-amber-600' : ''}`}>
                            {noCost ? '\u00A30.00' : formatCurrency(ln.line_value)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums" data-testid={`var-val-${idx}`}>
                            {ln.variance_value != null ? formatAccounting(ln.variance_value) : '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    {(() => {
                      const rows = filteredCalculationLines;
                      const isFiltered = (productSearch || productActiveFilters > 0);
                      const sumBookQty = rows.reduce((s, l) => s + (l.expected_qty || 0), 0);
                      const sumClosingQty = rows.reduce((s, l) => s + (l.base_quantity || 0), 0);
                      const sumVarQty = rows.reduce((s, l) => s + (l.variance_qty || 0), 0);
                      const sumBookCost = rows.reduce((s, l) => s + (l.expected_value || 0), 0);
                      const sumClosingCost = rows.reduce((s, l) => s + (l.line_value || 0), 0);
                      const sumVarVal = rows.reduce((s, l) => s + (l.variance_value || 0), 0);
                      const unallocated = calculation.unallocated_variance || 0;
                      return (
                        <>
                          <tr className="bg-slate-50 font-semibold border-t">
                            <td className="px-3 py-2">{isFiltered ? 'Filtered sub-total' : 'Sub-total (product variances)'}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{Number(sumBookQty).toLocaleString('en-GB', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{Number(sumClosingQty).toLocaleString('en-GB', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{formatQtyAccounting(sumVarQty)}</td>
                            <td></td>
                            <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(sumBookCost)}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(isFiltered ? sumClosingCost : calculation.total_value)}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{formatAccounting(sumVarVal)}</td>
                          </tr>
                          {!isFiltered && Math.abs(unallocated) >= 0.01 && (
                            <tr className="bg-amber-50/70 text-sm border-t" data-testid="unallocated-adj-row">
                              <td className="px-3 py-2 font-medium text-amber-700" colSpan={5}>Unallocated adjustments</td>
                              <td className="px-3 py-2 text-right"></td>
                              <td className="px-3 py-2 text-right"></td>
                              <td className="px-3 py-2 text-right tabular-nums">{formatAccounting(unallocated)}</td>
                            </tr>
                          )}
                          {!isFiltered && (
                            <tr className="bg-emerald-50 font-bold border-t-2">
                              <td className="px-3 py-2" colSpan={5}>Total Variance ({calculation.stocktake_result != null ? (calculation.stocktake_result >= 0 ? 'Surplus' : 'Deficit') : '—'})</td>
                              <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(calculation.expected_closing_value)}</td>
                              <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(calculation.total_value)}</td>
                              <td className="px-3 py-2 text-right tabular-nums text-lg">
                                {calculation.headline_fallback_reason && calculation.headline_stocktake_result == null
                                  ? <span className="text-slate-400">—</span>
                                  : formatAccounting(calculation.stocktake_result)}
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })()}
                  </tfoot>
                </table>
              </div>
              {calculation.lines.some(ln => ln.cost_source === 'no_cost') && (
                <div className="px-3 py-2 bg-amber-50 border-t text-xs text-amber-700">
                  Items marked "No cost" have no delivery history for this site. Record deliveries to populate site-level costs.
                </div>
              )}
            </div>
              </CollapsibleContent>
            </Collapsible>
          )}
        </CardContent>
      )}
      {calculation && !isAudit && (
        <CardContent>
          <p className="text-sm text-muted-foreground">Previous valuation calculation found. Click Calculate to run the full stock audit.</p>
        </CardContent>
      )}
      <StockAuditBreakdownDialog
        open={!!breakdownType}
        onClose={() => setBreakdownType(null)}
        breakdown={activeBreakdown}
      />
    </Card>
  );
}
